import { Link, useLocation } from '@tanstack/react-router';
import {
    Building2,
    Check,
    ChevronRight,
    ChevronUp,
    ChevronsUpDown,
    CreditCard,
    Languages,
    LogIn,
    LogOut,
    User
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandLogo } from '@/components/branding/brand-logo';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
    SidebarSeparator,
    useSidebar
} from '@/components/ui/sidebar';
import {
    getVisibleOrganizations,
    ORGANIZATION_MENU_PAGE_SIZE
} from '@/features/organizations/components/organization-menu-pagination';
import { useOrganizationBilling } from '@/features/organizations/hooks/useOrganizationBilling';
import { useOrganizations } from '@/features/organizations/hooks/useOrganizations';
import { canManageOrganizationBilling } from '@/features/organizations/permissions';
import { isLifetimeBilling } from '@/features/organizations/services/billing-source';
import { useCurrentOrganizationStore } from '@/features/organizations/store/useCurrentOrganizationStore';
import { i18n, LANGUAGE_OPTIONS, resolveSupportedLanguage, type SupportedLanguage } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/useAuth';
import { getIcon } from './icon-map';
import { getNavigationSections, isNavigationItemActive, isNavigationSubItemActive } from './nav-config';

function getInitials(value: string | null | undefined): string {
    if (!value) {
        return 'G';
    }

    return (
        value
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() ?? '')
            .join('') || 'G'
    );
}

export function AppSidebar() {
    const { t } = useTranslation();
    const location = useLocation();
    const { isMobile, setOpenMobile } = useSidebar();
    const { isAuthenticated, email, login, logout, token, username } = useAuth();
    const organizationsQuery = useOrganizations();
    const currentOrganizationId = useCurrentOrganizationStore((state) => state.currentOrganizationId);
    const setCurrentOrganizationId = useCurrentOrganizationStore((state) => state.setCurrentOrganizationId);
    const [visibleOrganizationCount, setVisibleOrganizationCount] = useState(ORGANIZATION_MENU_PAGE_SIZE);
    const hash = location.hash ?? window.location.hash ?? '';
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [isLanguageSectionOpen, setIsLanguageSectionOpen] = useState(false);
    const currentLanguage = resolveSupportedLanguage(i18n.language);
    const hasSession = Boolean(isAuthenticated || token || username || email);
    const hasNoOrganizations =
        hasSession && organizationsQuery.isSuccess && (organizationsQuery.data?.length ?? 0) === 0;
    const currentOrganization =
        organizationsQuery.data?.find((organization) => organization.id === currentOrganizationId) ?? null;
    const currentOrganizationRole = currentOrganization?.membership.role ?? null;
    const navigationSections = getNavigationSections(currentOrganizationRole, hasNoOrganizations);
    const billingQuery = useOrganizationBilling(currentOrganizationId);
    const canAccessBillingNavigation =
        (hasNoOrganizations || canManageOrganizationBilling(currentOrganizationRole)) &&
        !isLifetimeBilling(billingQuery.data);
    const visibleOrganizations = useMemo(
        () =>
            getVisibleOrganizations({
                organizations: organizationsQuery.data ?? [],
                currentOrganizationId,
                visibleCount: visibleOrganizationCount
            }),
        [currentOrganizationId, organizationsQuery.data, visibleOrganizationCount]
    );
    const hasMoreOrganizations = (organizationsQuery.data?.length ?? 0) > visibleOrganizations.length;

    function closeMobileSidebar() {
        if (isMobile) {
            setOpenMobile(false);
        }
    }

    return (
        <Sidebar collapsible='offcanvas' className='dark'>
            <SidebarHeader variant='appShell'>
                <SidebarMenu>
                    <SidebarMenuItem>
                        <SidebarMenuButton asChild variant='brand' size='brand'>
                            <Link to='/home' onClick={closeMobileSidebar}>
                                <BrandLogo showLabel variant='sidebar' className='max-w-[160px]' />
                            </Link>
                        </SidebarMenuButton>
                    </SidebarMenuItem>
                </SidebarMenu>
                {hasSession ? (
                    <DropdownMenu
                        onOpenChange={(open) => !open && setVisibleOrganizationCount(ORGANIZATION_MENU_PAGE_SIZE)}
                    >
                        <DropdownMenuTrigger asChild>
                            <SidebarMenuButton variant='organization' size='shell' className='mt-3'>
                                <Avatar variant='sidebarSquare' size='sidebarSm'>
                                    <AvatarFallback variant='sidebarSquare'>
                                        {getInitials(currentOrganization?.name ?? username ?? t('common.guest'))}
                                    </AvatarFallback>
                                </Avatar>
                                <div className='grid min-w-0 flex-1 text-left'>
                                    <span className='truncate text-sm text-sidebar-foreground'>
                                        {currentOrganization?.name ?? t('sidebar.noOrganization')}
                                    </span>
                                    <span className='truncate font-mono text-[10px] uppercase tracking-wider text-sidebar-foreground/40'>
                                        {currentOrganizationRole ?? 'Owner'}
                                    </span>
                                </div>
                                <ChevronsUpDown className='ml-auto size-4 text-sidebar-foreground/40' />
                            </SidebarMenuButton>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='start' variant='sidebar'>
                            <DropdownMenuLabel variant='sidebar'>{t('sidebar.organizationMenu')}</DropdownMenuLabel>
                            <DropdownMenuSeparator variant='sidebar' />
                            <div className='max-h-[320px] overflow-y-auto pr-1'>
                                {organizationsQuery.data?.length ? (
                                    visibleOrganizations.map((organization) => {
                                        const isCurrent = organization.id === currentOrganizationId;
                                        return (
                                            <DropdownMenuItem
                                                key={organization.id}
                                                variant='sidebar'
                                                onSelect={() => setCurrentOrganizationId(organization.id)}
                                            >
                                                <Building2 className='text-sidebar-foreground/40' />
                                                <span className='truncate'>{organization.name}</span>
                                                {isCurrent ? <Check className='ml-auto text-chart-1' /> : null}
                                            </DropdownMenuItem>
                                        );
                                    })
                                ) : (
                                    <DropdownMenuItem asChild variant='sidebar'>
                                        <Link to='/organizations' onClick={closeMobileSidebar}>
                                            <Building2 className='text-sidebar-foreground/40' />
                                            <span>{t('sidebar.noOrganization')}</span>
                                        </Link>
                                    </DropdownMenuItem>
                                )}
                            </div>
                            {hasMoreOrganizations ? (
                                <>
                                    <DropdownMenuSeparator variant='sidebar' />
                                    <div className='flex justify-center py-1'>
                                        <Button
                                            size='compact'
                                            variant='sidebarGhost'
                                            onClick={() =>
                                                setVisibleOrganizationCount(
                                                    (currentCount) => currentCount + ORGANIZATION_MENU_PAGE_SIZE
                                                )
                                            }
                                        >
                                            load more
                                        </Button>
                                    </div>
                                </>
                            ) : null}
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : null}
            </SidebarHeader>
            <SidebarContent variant='appShell'>
                {navigationSections.map((section, index) => (
                    <div key={section.title}>
                        {index > 0 ? <SidebarSeparator variant='section' /> : null}
                        <SidebarGroup variant='flush'>
                            <SidebarGroupLabel variant='eyebrow'>
                                {section.title === 'main' ? '// main' : '// setup'}
                            </SidebarGroupLabel>
                            <SidebarGroupContent>
                                <SidebarMenu variant='compact'>
                                    {section.items.map((item) => {
                                        const Icon = getIcon(item.icon);
                                        const routeActive = isNavigationItemActive(item, location.pathname);
                                        const activeSubItem = item.subItems?.find((subItem) =>
                                            isNavigationSubItemActive(subItem, location.pathname, hash)
                                        );
                                        const parentActive = routeActive && !activeSubItem;

                                        return (
                                            <SidebarMenuItem key={item.href}>
                                                <SidebarMenuButton
                                                    asChild
                                                    isActive={parentActive}
                                                    variant='navigation'
                                                    size='navigation'
                                                >
                                                    <Link to={item.href} onClick={closeMobileSidebar}>
                                                        <Icon className='size-4' />
                                                        <span>{t(`nav.${item.title}`)}</span>
                                                        {parentActive ? (
                                                            <span className='ml-auto size-1.5 rounded-full bg-chart-1' />
                                                        ) : null}
                                                    </Link>
                                                </SidebarMenuButton>
                                                {routeActive && item.subItems?.length ? (
                                                    <SidebarMenuSub variant='navigation'>
                                                        {item.subItems.map((subItem) => {
                                                            const SubIcon = getIcon(subItem.icon);
                                                            const subItemActive = isNavigationSubItemActive(
                                                                subItem,
                                                                location.pathname,
                                                                hash
                                                            );
                                                            return (
                                                                <SidebarMenuSubItem key={subItem.hash ?? subItem.href}>
                                                                    <SidebarMenuSubButton
                                                                        asChild
                                                                        isActive={subItemActive}
                                                                        size='md'
                                                                        variant='navigation'
                                                                    >
                                                                        <Link
                                                                            to={subItem.href}
                                                                            hash={subItem.hash as never}
                                                                            onClick={closeMobileSidebar}
                                                                        >
                                                                            <SubIcon />
                                                                            <span>{t(`nav.${subItem.title}`)}</span>
                                                                            {subItemActive ? (
                                                                                <span className='ml-auto size-1.5 rounded-full bg-chart-1' />
                                                                            ) : null}
                                                                        </Link>
                                                                    </SidebarMenuSubButton>
                                                                </SidebarMenuSubItem>
                                                            );
                                                        })}
                                                    </SidebarMenuSub>
                                                ) : null}
                                            </SidebarMenuItem>
                                        );
                                    })}
                                </SidebarMenu>
                            </SidebarGroupContent>
                        </SidebarGroup>
                    </div>
                ))}
            </SidebarContent>
            <SidebarFooter variant='appShell'>
                <SidebarMenu>
                    <SidebarMenuItem>
                        {hasSession ? (
                            <DropdownMenu
                                onOpenChange={(open) => {
                                    setIsProfileMenuOpen(open);
                                    if (!open) {
                                        setIsLanguageSectionOpen(false);
                                    }
                                }}
                            >
                                <DropdownMenuTrigger asChild>
                                    <SidebarMenuButton variant='profile' size='shell'>
                                        <Avatar variant='sidebarRound' size='md'>
                                            <AvatarFallback variant='sidebarRound'>
                                                {getInitials(username ?? email ?? t('common.guest'))}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className='grid min-w-0 flex-1 text-left'>
                                            <span className='truncate text-xs font-medium text-sidebar-foreground'>
                                                {username ?? email ?? t('common.guest')}
                                            </span>
                                            <span className='truncate font-mono text-[10px] uppercase tracking-wider text-sidebar-foreground/40'>
                                                {currentOrganizationRole ?? 'Owner'}
                                            </span>
                                        </div>
                                        <ChevronUp
                                            className={cn(
                                                'ml-auto size-4 text-sidebar-foreground/60 transition-transform',
                                                isProfileMenuOpen && 'rotate-180'
                                            )}
                                        />
                                    </SidebarMenuButton>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent side='top' align='end' sideOffset={10} variant='account'>
                                    <div className='overflow-hidden rounded-[20px] border border-white/12 bg-[#171717]'>
                                        <DropdownMenuLabel variant='account'>{t('sidebar.account')}</DropdownMenuLabel>
                                        <div className='px-3 pb-3'>
                                            <DropdownMenuItem asChild variant='account'>
                                                <Link to='/account-details' onClick={closeMobileSidebar}>
                                                    <User className='size-5 text-white/45' />
                                                    <span>{t('sidebar.accountDetails')}</span>
                                                </Link>
                                            </DropdownMenuItem>
                                            {canAccessBillingNavigation ? (
                                                <DropdownMenuItem asChild variant='account'>
                                                    <Link to='/pricing' onClick={closeMobileSidebar}>
                                                        <CreditCard className='size-5 text-white/45' />
                                                        <span>{t('nav.pricing')}</span>
                                                    </Link>
                                                </DropdownMenuItem>
                                            ) : null}
                                        </div>
                                        <DropdownMenuSeparator variant='account' />
                                        <div className='px-3 py-3'>
                                            <Collapsible
                                                open={isLanguageSectionOpen}
                                                onOpenChange={setIsLanguageSectionOpen}
                                            >
                                                <CollapsibleTrigger asChild>
                                                    <Button
                                                        variant='accountGhost'
                                                        size='account'
                                                        className='w-full text-left'
                                                    >
                                                        <Languages className='size-5 shrink-0 text-white/45' />
                                                        <span className='ml-3'>{t('sidebar.language')}</span>
                                                        <span className='ml-auto pr-3 font-mono text-[10px] font-normal tracking-[0.08em] text-white/55'>
                                                            {LANGUAGE_OPTIONS.find(
                                                                (language) => language.value === currentLanguage
                                                            )?.label ?? currentLanguage}
                                                        </span>
                                                        <ChevronRight
                                                            className={cn(
                                                                'size-4 text-white/45 transition-transform',
                                                                isLanguageSectionOpen && 'rotate-90'
                                                            )}
                                                        />
                                                    </Button>
                                                </CollapsibleTrigger>
                                                <CollapsibleContent>
                                                    <div className='pt-1 pb-1'>
                                                        <DropdownMenuRadioGroup
                                                            value={currentLanguage}
                                                            onValueChange={(value) =>
                                                                void i18n.changeLanguage(value as SupportedLanguage)
                                                            }
                                                        >
                                                            {LANGUAGE_OPTIONS.map((language) => (
                                                                <DropdownMenuRadioItem
                                                                    key={language.value}
                                                                    value={language.value}
                                                                    variant='account'
                                                                >
                                                                    <span>{language.label}</span>
                                                                    {currentLanguage === language.value ? (
                                                                        <Check className='absolute right-2.5 size-4 text-[#9db1ff]' />
                                                                    ) : null}
                                                                </DropdownMenuRadioItem>
                                                            ))}
                                                        </DropdownMenuRadioGroup>
                                                    </div>
                                                </CollapsibleContent>
                                            </Collapsible>
                                        </div>
                                        <DropdownMenuSeparator variant='account' />
                                        <div className='px-3 py-3'>
                                            <DropdownMenuItem variant='account' onSelect={() => void logout()}>
                                                <LogOut className='text-white/45' />
                                                <span>{t('sidebar.logout')}</span>
                                            </DropdownMenuItem>
                                        </div>
                                    </div>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <SidebarMenuButton variant='profile' size='shell' onClick={() => void login()}>
                                <Avatar variant='sidebarRound' size='md'>
                                    <AvatarFallback variant='sidebarRoundPlain'>
                                        {getInitials(username ?? t('common.guest'))}
                                    </AvatarFallback>
                                </Avatar>
                                <div className='grid min-w-0 flex-1 text-left'>
                                    <span className='truncate text-sm font-medium text-sidebar-foreground'>
                                        {t('sidebar.login')}
                                    </span>
                                    <span className='truncate font-mono text-[10px] uppercase tracking-wider text-sidebar-foreground/40'>
                                        {t('sidebar.openSignIn')}
                                    </span>
                                </div>
                                <LogIn className='ml-auto size-4 text-sidebar-foreground/60' />
                            </SidebarMenuButton>
                        )}
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>
    );
}
